import axios from 'axios'

const state = {
  lastUserId: null, // Track the last user ID to detect user changes
  googleMaps: {
    apiKey: null,
    status: null,
    isConfigured: false,
    isValid: false,
    message: null,
    lastValidatedAt: null,
    loading: false
  },
  valueSerp: {
    apiKey: null,
    status: null,
    isConfigured: false,
    isValid: false,
    message: null,
    lastValidatedAt: null,
    loading: false
  },
  dataForSeo: {
    apiKey: null,
    status: null,
    isConfigured: false,
    isValid: false,
    message: null,
    lastValidatedAt: null,
    loading: false
  }
}

const mutations = {
  SET_LAST_USER_ID(state, userId) {
    state.lastUserId = userId
  },

  SET_GOOGLE_MAPS_KEY(state, data) {
    if (data) {
      state.googleMaps = {
        apiKey: data.api_key || null,
        status: data.status || 0,
        isConfigured: data.is_configured || false,
        isValid: data.status === 1,
        message: data.message || null,
        lastValidatedAt: data.last_validated_at || null,
        loading: false
      }
    } else {
      // Reset to defaults if no data
      state.googleMaps = {
        apiKey: null,
        status: null,
        isConfigured: false,
        isValid: false,
        message: 'No API key configured',
        lastValidatedAt: null,
        loading: false
      }
    }
  },
  
  SET_LOADING(state, { service, loading }) {
    if (service === 'googleMaps') {
      state.googleMaps.loading = loading
    } else if (service === 'valueSerp') {
      state.valueSerp.loading = loading
    }
  },
  
  SET_VALUE_SERP_KEY(state, data) {
    if (data) {
      state.valueSerp = {
        apiKey: data.api_key || null,
        status: data.status || 0,
        isConfigured: data.is_configured || false,
        isValid: data.status === 1,
        message: data.message || null,
        lastValidatedAt: data.last_validated_at || null,
        loading: false
      }
    } else {
      // Reset to defaults if no data
      state.valueSerp = {
        apiKey: null,
        status: null,
        isConfigured: false,
        isValid: false,
        message: 'No API key configured',
        lastValidatedAt: null,
        loading: false
      }
    }
  },
  
  SET_DATAFORSEO_KEY(state, data) {
    if (data) {
      state.dataForSeo = {
        apiKey: data.api_key || null,
        status: data.status || 0,
        isConfigured: data.is_configured || false,
        isValid: data.status === 1,
        message: data.message || null,
        lastValidatedAt: data.last_validated_at || null,
        loading: false
      }
    } else {
      // Reset to defaults if no data
      state.dataForSeo = {
        apiKey: null,
        status: null,
        isConfigured: false,
        isValid: false,
        message: 'No API key configured',
        lastValidatedAt: null,
        loading: false
      }
    }
  },

  CLEAR_API_KEYS(state) {
    state.lastUserId = null
    state.googleMaps = {
      apiKey: null,
      status: null,
      isConfigured: false,
      isValid: false,
      message: null,
      lastValidatedAt: null,
      loading: false
    }
    state.valueSerp = {
      apiKey: null,
      status: null,
      isConfigured: false,
      isValid: false,
      message: null,
      lastValidatedAt: null,
      loading: false
    }
    state.dataForSeo = {
      apiKey: null,
      status: null,
      isConfigured: false,
      isValid: false,
      message: null,
      lastValidatedAt: null,
      loading: false
    }
  }
}

const actions = {
  async fetchGoogleMapsKey({ commit, rootState, rootGetters }) {
    commit('SET_LOADING', { service: 'googleMaps', loading: true })

    try {
      // If user is authenticated, try to get their API key
      const token = rootGetters['auth/jwt'] || rootGetters['auth/accessToken'];
      if (token) {
        const userId = rootState.auth.user && rootState.auth.user.id
        // Fetching Google Maps key for user

        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/google-maps`,
          {
            headers: {
              'Authorization': 'Bearer ' + token
            }
          }
        )

        // Google Maps API response received

        if (response.data.success) {
          commit('SET_GOOGLE_MAPS_KEY', response.data.data)
          // Set the last user ID to track user changes
          if (userId) {
            commit('SET_LAST_USER_ID', userId)
          }
          return response.data.data
        }
      } else {
        // If not authenticated, get the public API key
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/google-maps/public`
        )
        
        if (response.data.success) {
          commit('SET_GOOGLE_MAPS_KEY', response.data.data)
          return response.data.data
        }
      }
    } catch (error) {
      console.error('Failed to fetch Google Maps API key:', error)
      commit('SET_GOOGLE_MAPS_KEY', null)
    } finally {
      commit('SET_LOADING', { service: 'googleMaps', loading: false })
    }
  },
  
  async fetchValueSerpKey({ commit, rootState, rootGetters }) {
    commit('SET_LOADING', { service: 'valueSerp', loading: true })

    try {
      const token = rootGetters['auth/jwt'] || rootGetters['auth/accessToken'];
      if (!token) {
        // No token available for ValueSerp fetch
        commit('SET_VALUE_SERP_KEY', null)
        return
      }

      const userId = rootState.auth.user && rootState.auth.user.id
      // Fetching ValueSerp key for user

      const response = await axios.get(
        `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/value-serp`,
        {
          headers: {
            'Authorization': 'Bearer ' + token
          }
        }
      )

      // ValueSerp API response received

      if (response.data.success && response.data.data) {
        commit('SET_VALUE_SERP_KEY', response.data.data)
        return response.data.data
      } else {
        // No data or not configured - explicitly set to null
        commit('SET_VALUE_SERP_KEY', null)
      }
    } catch (error) {
      console.error('Failed to fetch Value SERP API key:', error)
      // On error, explicitly reset the state
      commit('SET_VALUE_SERP_KEY', null)
    } finally {
      commit('SET_LOADING', { service: 'valueSerp', loading: false })
    }
  },

  async fetchDataForSeoKey({ commit, rootState, rootGetters }) {
    commit('SET_LOADING', { service: 'dataForSeo', loading: true })

    try {
      // First try to get from Supabase directly if we have a session
      const { supabase } = await import('@/config/supabase')
      const { data: { session } } = await supabase.auth.getSession()

      if (session && session.user) {
        // Try to get from user metadata first (fastest)
        const userMetadata = session.user.user_metadata || {}

        if (userMetadata.dataForSeoUsername && userMetadata.dataForSeoPassword) {
          // Create Base64 encoded API key from metadata
          const apiKey = btoa(`${userMetadata.dataForSeoUsername}:${userMetadata.dataForSeoPassword}`)

          commit('SET_DATAFORSEO_KEY', {
            username: userMetadata.dataForSeoUsername,
            api_key: apiKey,
            status: 1,
            is_configured: true
          })
          return {
            username: userMetadata.dataForSeoUsername,
            api_key: apiKey,
            status: 1,
            is_configured: true
          }
        }

        // If not in metadata, try user_profiles table
        const { data: userProfile, error } = await supabase
          .from('user_profiles')
          .select('data_for_seo_username, data_for_seo_password')
          .eq('id', session.user.id)
          .single()

        if (!error && userProfile && userProfile.data_for_seo_username && userProfile.data_for_seo_password) {
          const apiKey = btoa(`${userProfile.data_for_seo_username}:${userProfile.data_for_seo_password}`)

          commit('SET_DATAFORSEO_KEY', {
            username: userProfile.data_for_seo_username,
            api_key: apiKey,
            status: 1,
            is_configured: true
          })
          return {
            username: userProfile.data_for_seo_username,
            api_key: apiKey,
            status: 1,
            is_configured: true
          }
        }
      }

      // Fall back to backend API if no Supabase session or no data found
      const token = rootGetters['auth/jwt'] || rootGetters['auth/accessToken'];
      if (token) {
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/dataforseo`,
          {
            headers: {
              'Authorization': 'Bearer ' + token
            }
          }
        )

        if (response.data.success && response.data.data) {
          commit('SET_DATAFORSEO_KEY', response.data.data)
          return response.data.data
        }
      }

      // No credentials found
      commit('SET_DATAFORSEO_KEY', null)
    } catch (error) {
      console.error('Failed to fetch DataForSeo credentials:', error)
      commit('SET_DATAFORSEO_KEY', null)
    } finally {
      commit('SET_LOADING', { service: 'dataForSeo', loading: false })
    }
  },
  
  async saveGoogleMapsKey({ commit, rootState, rootGetters }, apiKey) {
    try {
      const token = rootGetters['auth/jwt'] || rootGetters['auth/accessToken'];
      const response = await axios.post(
        `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/google-maps`,
        {
          api_key: apiKey
        },
        {
          headers: {
            'Authorization': 'Bearer ' + token
          }
        }
      )
      
      if (response.data.success) {
        commit('SET_GOOGLE_MAPS_KEY', response.data.data)
        return response.data
      }
    } catch (error) {
      console.error('Failed to save Google Maps API key:', error)
      throw error
    }
  },
  
  async validateGoogleMapsKey({ commit, rootState, rootGetters }) {
    try {
      const token = rootGetters['auth/jwt'] || rootGetters['auth/accessToken'];
      const response = await axios.post(
        `${process.env.VUE_APP_API_ENDPOINT}/api/users/api-keys/google-maps/validate`,
        {},
        {
          headers: {
            'Authorization': 'Bearer ' + token
          }
        }
      )
      
      if (response.data.success || response.data.data) {
        // Update the status based on validation
        commit('SET_GOOGLE_MAPS_KEY', {
          ...state.googleMaps,
          status: response.data.data.status,
          isValid: response.data.data.status === 1,
          message: response.data.message,
          lastValidatedAt: response.data.data.last_validated_at
        })
        
        return response.data
      }
    } catch (error) {
      console.error('Failed to validate Google Maps API key:', error)
      throw error
    }
  },
  
  clearApiKeys({ commit }) {
    commit('CLEAR_API_KEYS')
  }
}

const getters = {
  googleMapsKey: state => state.googleMaps.apiKey,
  googleMapsStatus: state => state.googleMaps.status,
  isGoogleMapsConfigured: state => state.googleMaps.isConfigured,
  isGoogleMapsValid: state => state.googleMaps.isValid,
  googleMapsMessage: state => state.googleMaps.message,
  googleMapsLoading: state => state.googleMaps.loading,
  
  valueSerpKey: state => state.valueSerp.apiKey,
  valueSerpStatus: state => state.valueSerp.status,
  isValueSerpConfigured: state => state.valueSerp.isConfigured,
  isValueSerpValid: state => state.valueSerp.isValid,
  valueSerpMessage: state => state.valueSerp.message,
  valueSerpLoading: state => state.valueSerp.loading,

  dataForSeoKey: state => state.dataForSeo.apiKey,
  dataForSeoStatus: state => state.dataForSeo.status,
  isDataForSeoConfigured: state => state.dataForSeo.isConfigured,
  isDataForSeoValid: state => state.dataForSeo.isValid,
  dataForSeoMessage: state => state.dataForSeo.message,
  dataForSeoLoading: state => state.dataForSeo.loading
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}